'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { processHeading } from '@workspace/lib/heading-utils';
import { getBackgroundClass } from '@workspace/lib';
import { ProductCarouselProps, ProductCarouselSlide } from './product-carousel.types';

export interface UseProductCarouselReturn {
  // Data
  slides: ProductCarouselSlide[];
  mobileSlideGroups: ProductCarouselSlide[][];
  resolvedHeadingText: string | null;
  backgroundClass: string;
  layoutOptions: 1 | 2;

  // Mobile carousel
  viewportRefMobile: (node: HTMLElement | null) => void;
  selectedIndexMobile: number;
  snapCountMobile: number;
  goPrevMobile: () => void;
  goNextMobile: () => void;
  goToSlideMobile: (index: number) => void;

  // Desktop carousel
  viewportRefDesktop: (node: HTMLElement | null) => void;
  selectedIndexDesktop: number;
  snapCountDesktop: number;
  goPrevDesktop: () => void;
  goNextDesktop: () => void;
  goToSlideDesktop: (index: number) => void;
}

export function useProductCarousel({ block }: ProductCarouselProps): UseProductCarouselReturn {
  const { backgroundColor, productCarouselItems: slides = [], layoutOptions = 2, heading } = block;

  const { text: resolvedHeadingText } = processHeading(heading);
  const backgroundClass = getBackgroundClass(backgroundColor);

  // Chunk helper (mobile groups of 3 cards per slide)
  const mobileSlideGroups = useMemo(() => {
    const groups: ProductCarouselSlide[][] = [];
    if (!slides || slides.length === 0) return groups;
    for (let i = 0; i < slides.length; i += 3) {
      groups.push(slides.slice(i, i + 3));
    }
    return groups;
  }, [slides]);

  // Embla instances — separate for mobile and desktop to keep behavior simple
  // Mobile: each slide contains 3 cards, scroll 1 slide at a time
  const [viewportRefMobile, emblaMobile] = useEmblaCarousel({
    loop: true,
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
  });
  // Desktop: original behavior (single cards), scroll 1 card at a time
  const [viewportRefDesktop, emblaDesktop] = useEmblaCarousel({
    loop: true,
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
  });

  // State for mobile carousel
  const [selectedIndexMobile, setSelectedIndexMobile] = useState(0);
  const [snapCountMobile, setSnapCountMobile] = useState(0);
  // State for desktop carousel
  const [selectedIndexDesktop, setSelectedIndexDesktop] = useState(0);
  const [snapCountDesktop, setSnapCountDesktop] = useState(0);

  const onSelectMobile = useCallback(() => {
    if (!emblaMobile) return;
    setSelectedIndexMobile(emblaMobile.selectedScrollSnap());
  }, [emblaMobile]);
  const onSelectDesktop = useCallback(() => {
    if (!emblaDesktop) return;
    setSelectedIndexDesktop(emblaDesktop.selectedScrollSnap());
  }, [emblaDesktop]);

  // Bind mobile instance
  useEffect(() => {
    if (!emblaMobile) return;
    setSnapCountMobile(emblaMobile.scrollSnapList().length);
    onSelectMobile();
    emblaMobile.on('select', onSelectMobile);
    emblaMobile.on('reInit', () => {
      setSnapCountMobile(emblaMobile.scrollSnapList().length);
      onSelectMobile();
    });
    return () => {
      emblaMobile?.off('select', onSelectMobile);
    };
  }, [emblaMobile, onSelectMobile]);

  // Bind desktop instance
  useEffect(() => {
    if (!emblaDesktop) return;
    setSnapCountDesktop(emblaDesktop.scrollSnapList().length);
    onSelectDesktop();
    emblaDesktop.on('select', onSelectDesktop);
    emblaDesktop.on('reInit', () => {
      setSnapCountDesktop(emblaDesktop.scrollSnapList().length);
      onSelectDesktop();
    });
    return () => {
      emblaDesktop?.off('select', onSelectDesktop);
    };
  }, [emblaDesktop, onSelectDesktop]);

  const goPrevMobile = useCallback(() => emblaMobile?.scrollPrev(), [emblaMobile]);
  const goNextMobile = useCallback(() => emblaMobile?.scrollNext(), [emblaMobile]);
  const goToSlideMobile = useCallback((index: number) => emblaMobile?.scrollTo(index), [emblaMobile]);
  const goPrevDesktop = useCallback(() => emblaDesktop?.scrollPrev(), [emblaDesktop]);
  const goNextDesktop = useCallback(() => emblaDesktop?.scrollNext(), [emblaDesktop]);
  const goToSlideDesktop = useCallback((index: number) => emblaDesktop?.scrollTo(index), [emblaDesktop]);

  return {
    slides,
    mobileSlideGroups,
    resolvedHeadingText,
    backgroundClass,
    layoutOptions,
    viewportRefMobile,
    selectedIndexMobile,
    snapCountMobile,
    goPrevMobile,
    goNextMobile,
    goToSlideMobile,
    viewportRefDesktop,
    selectedIndexDesktop,
    snapCountDesktop,
    goPrevDesktop,
    goNextDesktop,
    goToSlideDesktop,
  };
}
